import { useRef, useEffect, useState } from 'react';
import RichText from 'components/shared/richtext';
import Button from 'components/shared/button';
import useIsMobile from 'hooks/utils/useIsMobile';
import BuyingPros from 'components/shared/buyingPros';
import Breadcrumbs from 'components/layout/breadcrumbs/breadcrumbs';
import SiteLink from 'components/shared/sitelink/sitelink';
import Image from 'components/shared/image/image';
import { useRouter } from 'next/router';
import { addLocaleToHref } from 'lib/utils/helpers';
import cn from 'classnames';
import WishlistButton from 'components/shared/wishlist-button';
import UnitPrice from 'components/shared/unit-price/unitPrice';
import AttributeTag from '../../product/attributeTag/attributeTag';
import styles from './productBundleInfo.module.scss';
import BundleCartSection from '../bundleCartSection/bundleCartSection';
import BundleVariations from '../bundleVariations';

const ProductBundleInfo = ({ locale, bundlePrices, shopifyData, productBundleData, resources }) => {
  const {
    title,
    breadcrumbs,
    description,
    productAttributes,
    productTags,
    bundleSlogan,
    linkToExternalWebshop,
    slug,
  } = productBundleData;

  const isMobile = useIsMobile();
  const cartSectionRef = useRef(null);
  const router = useRouter();

  const { buyingProsSection, outOfStock: outOfStockMessage } = resources.productpage;
  const { onlyAvailableAtSalon, findASalonButton, dontShipToDestination, contactUsButton } =
    resources.secondpartproductpage;
  const { localesWithWebshop, localesWithSalonFinder, saleText } = resources.settings;

  const localeHasWebshop = localesWithWebshop.onOrOff;
  const localeHasSalonFinder = localesWithSalonFinder.onOrOff;
  const [scrolledDown, setScrolledDown] = useState(false);
  const lastScrollTop = useRef(0);

  const isUnclickable = ({ slug }) => {
    const forbiddenWords = ['keune-stieglitz-giftbox', 'so-pure-box'];
    return forbiddenWords.some(word => slug.includes(word));
  };

  useEffect(() => {
    document.body.style.paddingBottom = isMobile && localeHasWebshop ? '6rem' : '';
    return () => {
      document.body.style.paddingBottom = '';
    };
  }, [isMobile, localeHasWebshop]);

  // detect if the user is scrolling down
  // needed to show/hide the add to cart button on mobile
  useEffect(() => {
    const handleScroll = () => {
      const st = window.pageYOffset || document.documentElement.scrollTop;
      if (st > lastScrollTop.current && lastScrollTop.current > 0) {
        setScrolledDown(true);
      }
      lastScrollTop.current = st <= 0 ? 0 : st;
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    setScrolledDown(false);
    lastScrollTop.current = 0;
  }, [slug]);

  return (
    <div>
      {!isMobile && breadcrumbs && (
        <Breadcrumbs locale={locale} className={styles.productBreadcrumbs} data={breadcrumbs} />
      )}
      <div className={styles.productTagsRow}>
        {productTags?.map(tag => (
          <div className={styles.productTag}>{tag}</div>
        ))}
      </div>
      {localeHasWebshop && !bundlePrices.outOfStock && (
        <div className={styles.productTagsRow}>
          {!!bundlePrices.compareAtPrice && bundlePrices.salePercentage > 0 && (
            <div className={styles.salesTag}>
              {`${saleText?.text || ''} ${bundlePrices.salePercentage} %`}
            </div>
          )}
          {productTags?.map(tag => (
            <div key={`Product tag ${tag}`} className={styles.productTag}>
              {tag}
            </div>
          ))}
        </div>
      )}
      {localeHasWebshop && bundlePrices.outOfStock && (
        <div className={styles.productTagsRow}>
          <div className={styles.outOfStockTag}>{outOfStockMessage.text}</div>
        </div>
      )}
      <div className={styles.productHeader}>
        <h1 className={styles.productName}>{title}</h1>
        {localeHasWebshop && (
          <WishlistButton
            className={styles.wishlistIcon}
            productData={productBundleData}
            shopifyData={shopifyData}
          />
        )}
      </div>
      {bundleSlogan && (
        <div className={styles.variantTitleAndEAN}>
          <p className={styles.singleVariant}>{bundleSlogan}</p>
        </div>
      )}
      {isMobile ? (
        <>
          <BundleVariations
            locale={locale}
            productBundleData={productBundleData}
            resources={resources}
            localeHasWebshop={localeHasWebshop}
          />
          <UnitPrice
            price={bundlePrices.price}
            locale={locale}
            className={styles.unitPrice}
            globalModules={resources}
          />
        </>
      ) : null}
      <div className="wae-rating" />

      {localeHasSalonFinder && !localeHasWebshop && !linkToExternalWebshop?.externalLink && (
        <>
          <h1 className={styles.onlyAvailableAtSalonText}>{onlyAvailableAtSalon.text}</h1>
          {isMobile && (
            <Button
              locale={locale}
              className={styles.findASalonButton}
              button={findASalonButton.button}
            />
          )}
        </>
      )}
      {!localeHasSalonFinder && !localeHasWebshop && !linkToExternalWebshop?.externalLink && (
        <>
          <p className={styles.dontShipToDestinationText}>{dontShipToDestination.text}</p>
          {isMobile && (
            <Button
              locale={locale}
              className={styles.contactUsButton}
              button={contactUsButton.button}
            />
          )}
        </>
      )}
      <div className={styles.productsSection}>
        {productBundleData.products.map(product => (
          <SiteLink
            key={product.slug}
            locale={locale}
            href={addLocaleToHref(`p/${product.slug}`, locale, router)}
            className={cn(styles.singleProduct, isUnclickable(product) && styles.pointerEventNone)}
          >
            <Image
              src={product.thumbnailImage}
              className={styles.singleProductImage}
              layout="fill"
            />
            <div className={styles.singleProductInfo}>
              <h2 className={styles.singleProductTitle}>{product.title}</h2>
              <p className={styles.singleProductSize}>{product.size}</p>
            </div>
          </SiteLink>
        ))}
      </div>
      <div ref={cartSectionRef}>
        {isMobile && (
          <>
            {!localeHasWebshop && linkToExternalWebshop?.externalLink && (
              <>
                <h1 className={styles.dontShipToDestinationText}>{dontShipToDestination.text}</h1>
                <Button
                  locale={locale}
                  className={styles.findASalonButton}
                  button={linkToExternalWebshop}
                />
              </>
            )}
          </>
        )}
      </div>
      <RichText className={styles.productDescription} text={description.json} />
      <div className={styles.productAttributes}>
        {productAttributes?.map(attribute => (
          <AttributeTag className={styles.attributeTag} key={attribute} attribute={attribute} />
        ))}
      </div>
      {!isMobile ? (
        <>
          <BundleVariations
            locale={locale}
            productBundleData={productBundleData}
            resources={resources}
            localeHasWebshop={localeHasWebshop}
          />
          <UnitPrice
            price={bundlePrices.price}
            locale={locale}
            className={styles.unitPrice}
            globalModules={resources}
          />
        </>
      ) : null}
      {localeHasWebshop && (
        <div
          className={cn({
            [styles.fixedCartSectionWrapper]: isMobile,
            [styles.fixedCartSectionWrapperVisible]: isMobile && scrolledDown,
          })}
        >
          <BundleCartSection
            outOfStock={bundlePrices.outOfStock}
            resources={resources}
            className={styles.fixedCartSection}
            shopifyData={shopifyData}
            productBundleData={productBundleData}
            bundlePrices={bundlePrices}
            locale={locale}
          />
        </div>
      )}
      {buyingProsSection?.sections[0] && <BuyingPros data={buyingProsSection.sections[0]} />}
    </div>
  );
};

export default ProductBundleInfo;
